import React, { useEffect, useMemo, useState } from "react";

import PlateauxGrid from "../Plateaux/PlateauxGrid";
import PlateauConfigurator from "../Plateaux/PlateauConfigurator";

import { plateauxData } from "../../utils/plateauxData";
import { trackFunnelEventBatch } from "../../utils/trackFunnelBatch";

/* ============================================================
   Lecture de la langue active
   ============================================================

   Ce bloc lit la langue courante depuis le localStorage.
*/
function getCurrentLang() {
  if (typeof window === "undefined") return "fr";

  const lang = localStorage.getItem("lang");
  return ["fr", "de", "en"].includes(lang) ? lang : "fr";
}

/* ============================================================
   Lecture d’un champ localisé
   ============================================================

   Ce bloc retourne la bonne valeur selon la langue active.
*/
function getLocalizedValue(value, lang = "fr") {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value?.[lang] || value?.fr || value?.de || value?.en || "";
  }
  return "";
}

/* ============================================================
   Comparaison d’un plateau avec une préconfiguration
   ============================================================

   Ce bloc sert à retrouver automatiquement le plateau
   qui correspond à une ancienne commande.
*/
function matchesInitialPlateauConfig(plateau, initialConfig) {
  if (!plateau || !initialConfig) return false;

  const plateauId = String(plateau?.id || "").trim();
  const plateauSlug = String(plateau?.slug || "").trim();
  const plateauCode = String(plateau?.code || "").trim();

  const sourceId = String(initialConfig?.sourcePlateauId || "").trim();
  const sourceSlug = String(initialConfig?.sourcePlateauSlug || "").trim();
  const sourceCode = String(initialConfig?.sourcePlateauCode || "").trim();

  return (
    (sourceId && plateauId && sourceId === plateauId) ||
    (sourceSlug && plateauSlug && sourceSlug === plateauSlug) ||
    (sourceCode && plateauCode && sourceCode === plateauCode)
  );
}

export default function PlateauxEvenementsView({
  t = {},
  initialReorderConfig = null,
  onReorderConfigConsumed,
  setVueActuelle,
}) {
  /* ============================================================
     État du plateau sélectionné
     ============================================================

     Ce bloc conserve le plateau actuellement actif.
  */
  const [plateauSelectionne, setPlateauSelectionne] = useState(null);

  const hasPlateaux = Array.isArray(plateauxData) && plateauxData.length > 0;
  const lang = getCurrentLang();

  /* ============================================================
     Texte d’introduction
     ============================================================

     Ce bloc produit l’introduction éditoriale de la page.
  */
  const texteIntroduction = useMemo(() => {
    return t?.plateauxPageIntro
      ? t.plateauxPageIntro
      : "Découvrez nos plateaux artisanaux pensés pour vos événements, apéritifs et repas conviviaux. Commencez par choisir le type de plateau qui correspond à votre occasion. Vous pourrez ensuite le personnaliser avant de l’ajouter au panier.";
  }, [t]);

  /* ============================================================
     Nom du plateau sélectionné
     ============================================================

     Ce bloc calcule le nom localisé du plateau actif.
  */
  const nomPlateauSelectionne = useMemo(() => {
    return getLocalizedValue(plateauSelectionne?.nom, lang);
  }, [plateauSelectionne, lang]);

  /* ============================================================
     Injection automatique d’une ancienne configuration
     ============================================================

     Ce bloc sélectionne automatiquement le bon plateau
     quand l’utilisateur arrive depuis l’historique client.
  */
  useEffect(() => {
    if (!initialReorderConfig) return;
    if (!Array.isArray(plateauxData) || plateauxData.length === 0) return;

    const matchedPlateau =
      plateauxData.find((plateau) =>
        matchesInitialPlateauConfig(plateau, initialReorderConfig)
      ) || null;

    if (matchedPlateau) {
      setPlateauSelectionne(matchedPlateau);
    }
  }, [initialReorderConfig]);

  /* ============================================================
     Scroll automatique vers le configurateur
     ============================================================

     Ce bloc amène l’utilisateur vers l’étape 2 après sélection.
  */
  useEffect(() => {
    if (!plateauSelectionne) return;

    const timer = window.setTimeout(() => {
      const target = document.querySelector(".plateaux-view-configurator");
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 180);

    return () => window.clearTimeout(timer);
  }, [plateauSelectionne]);

  /* ============================================================
     Préconfiguration réellement transmise au configurateur
     ============================================================

     Ce bloc évite d’injecter une config si elle ne correspond
     pas au plateau actuellement sélectionné.
  */
  const effectiveInitialConfig = useMemo(() => {
    if (!plateauSelectionne || !initialReorderConfig) return null;

    const isMatch = matchesInitialPlateauConfig(
      plateauSelectionne,
      initialReorderConfig
    );

    return isMatch ? initialReorderConfig : null;
  }, [plateauSelectionne, initialReorderConfig]);

  const handleSelectPlateau = (plateau) => {
    setPlateauSelectionne(plateau);
    trackFunnelEventBatch("view_plateau_configurator", {
      item_type: "plateau_configurator",
      product_scope: "plateau",
      product_id: plateau?.id || plateau?.slug || "plateau_configurator",
      product_name: getLocalizedValue(plateau?.nom, lang) || "Plateau configuré",
      category: "Plateaux événements",
      plateau_type: plateau?.slug || plateau?.code || plateau?.id || "",
      requires_24h: true,
    });
  };

  return (
    <div className="plateaux-page-shell">
      {/* ======================================================
         Hero / intro
         ====================================================== */}
      <section className="plateaux-view-hero">
        <div className="plateaux-view-kicker">
          {t?.plateauxPageKicker || "Plateaux événements"}
        </div>

        <h1 className="plateaux-view-title">
          {t?.plateauxPageTitle || "Nos plateaux événements"}
        </h1>

        <p className="plateaux-view-intro">{texteIntroduction}</p>

        <div className="plateaux-view-hero-media">
          <img
            src="/svg-plateauconfigurator.svg"
            alt={t?.plateauxHeroAlt || "Illustration plateaux événements"}
            className="plateaux-view-hero-image"
          />
        </div>
      </section>

      {/* ======================================================
         Étape 1 — sélection du plateau
         ====================================================== */}
      <section className="plateaux-view-selection">
        <div className="plateaux-view-section-head">
          <div>
            <div className="plateaux-view-section-kicker">
              {t?.plateauxSelectionKicker || "Étape 1"}
            </div>

            <h2 className="plateaux-view-section-title">
              {t?.plateauxSelectionTitle || "Choisir un type de plateau"}
            </h2>
          </div>

          {plateauSelectionne ? (
            <div className="plateaux-selected-chip">
              {t?.plateauxSelectedLabel || "Plateau sélectionné"} :{" "}
              <strong>{nomPlateauSelectionne}</strong>
            </div>
          ) : null}
        </div>

        {!hasPlateaux ? (
          <div className="plateaux-empty-state">
            {t?.plateauxEmptyState ||
              "Aucun plateau n’est disponible pour le moment."}
          </div>
        ) : (
          <>
            <PlateauxGrid
              plateaux={plateauxData}
              plateauSelectionne={plateauSelectionne}
              onSelect={handleSelectPlateau}
              t={t}
            />

            {!plateauSelectionne ? (
              <div className="plateaux-selection-hint">
                {t?.plateauxSelectionHint ||
                  "Sélectionnez un plateau pour commencer la configuration."}
              </div>
            ) : null}
          </>
        )}
      </section>

      {/* ======================================================
         Étape 2 — configurateur
         ====================================================== */}
      {plateauSelectionne ? (
        <section className="plateaux-view-configurator">
          <div className="plateaux-view-section-head">
            <div>
              <div className="plateaux-view-section-kicker">
                {t?.plateauxConfigKicker || "Étape 2"}
              </div>

              <h2 className="plateaux-view-section-title">
                {t?.plateauxConfigTitle || "Configurer votre plateau"}
              </h2>
            </div>

            <button
              type="button"
              className="btn-luxury-outline plateaux-change-btn"
              onClick={() => {
                const target = document.querySelector(
                  ".plateaux-view-selection"
                );
                if (target) {
                  target.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }}
            >
              {t?.plateauxChangeSelection || "Voir les autres plateaux"}
            </button>
          </div>

          <PlateauConfigurator
            plateau={plateauSelectionne}
            t={t}
            initialConfig={effectiveInitialConfig}
            onInitialConfigConsumed={onReorderConfigConsumed}
            onGoToShop={() => setVueActuelle?.("magasin")}
            onGoToCave={() => setVueActuelle?.("alcool")}
          />
        </section>
      ) : null}
    </div>
  );
}
